import { action, makeObservable, observable, runInAction, toJS } from "mobx";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { message } from "antd";
import { cloneDeep, groupBy, isNil, pickBy } from "lodash";
import i18n, { I18N_NS } from "Config/i18n";
import { UploadProps } from "antd/lib/upload/interface";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { contactsStore } from "Stores/ContactsStore";
interface BcfServerFileWithFileStatus extends API_BcfServerFile {
  fileStatus?: API_BCFIMPORTFILE_FILESTATUS;
  modelListId?: number;
}

interface BcfImportModelsToDelete {
  guid?: string;
  modelListId?: number;
}
export interface TopicsWithConflicts extends API_BcfConflictTopic {
  files: BcfServerFileWithFileStatus[] & API_BcfImportFile[];
  viewData: {
    conflicts: API_BcfImportRequestConflict[];
    fileId: number;
    mandatoryWarningsAmount: number;
    optionalWarningsAmount: number;
  };
}

class BCFIssuesImportStore extends CoreStore {
  topicsWithConflicts: TopicsWithConflicts[] = [];
  bcfTempFiles: API_UploadFile[] = [];
  fileLoading: boolean = false;
  importLoading: boolean = false;
  BcfImportModelsToDelete: BcfImportModelsToDelete[] = [];
  submitImport: boolean = false; // flag for submit in import modal

  constructor() {
    super();
    makeObservable(this, {
      uploadIssues: action.bound,
      topicsWithConflicts: observable,
      processTopicsAndConflictsData: action.bound,
      resetConflictsAndFiles: action.bound,
      fileLoading: observable,
      updateConflicts: action.bound,
      importIssue: action.bound,
      removeViewpointFromTopic: action.bound,
      BcfImportModelsToDelete: observable,
      setModelsForDelete: action.bound,
      resetModelsForDelete: action.bound,
      clear: action.bound,
      submitImport: observable,
    });
  }

  url() {
    return "";
  }

  clear() {
    this.topicsWithConflicts = [];
    this.bcfTempFiles = [];
    this.fileLoading = false;
    this.BcfImportModelsToDelete = [];
    this.submitImport = false;
  }

  async uploadIssues(options: Parameters<UploadProps<API_BcfImportResponse>["customRequest"]>[0]) {
    try {
      this.fileLoading = true;
      const formData = new FormData();
      formData.append("file", options.file);
      formData.append("topicsAndConflicts", "");
      const response: API_BcfImportResponse = await API.newData(
        API_URL.BCF_SERVER_EXTENSIONS.importBcfDataWithTempFile(projectsDataStore.projectId),
        formData,
        {},
        {
          onUploadProgress: async (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            options.onProgress({ percent: percentCompleted });
          },
        }
      );
      options.onSuccess(response);
      this.processTopicsAndConflictsData(response.tempFile, response.topicsAndConflicts);

      message.success(i18n.t("Uploaded", { ns: I18N_NS.PROJECT }) as string);
    } catch (error) {
      throw error;
    } finally {
      this.fileLoading = false;
    }
  }

  updateConflicts(values: API_BcfServerTopic, keys?: string[], isMultiEdit?: boolean) {
    this.processModelsToDelete();
    const guids = keys ?? [values.guid];
    for (const guid of guids) {
      const item = this.topicsWithConflicts.find((topic) => topic.guid === guid);
      if (item === undefined) continue;

      const mergedValues = isMultiEdit ? pickBy(values, (value) => !isNil(value)) : values;
      Object.assign(item, mergedValues);
      this.normalizeTopicFieldTypes(item, mergedValues as API_BcfServerTopic);
      this.resolveConflictValues(item, values);
      this.recalculateWarnings(item);
    }
  }

  /**
   * Ensures topic field types match the API contract after form values merge.
   * Form widgets may produce different runtime types (e.g. dayjs for dates).
   * Also syncs assigned_to (user name) when assigned_to_id changes.
   */
  private normalizeTopicFieldTypes(item: TopicsWithConflicts, submittedValues: API_BcfServerTopic) {
    if ("assigned_to_id" in submittedValues && submittedValues.assigned_to_id != null) {
      item.assigned_to_id = Number(submittedValues.assigned_to_id);
      const userName = contactsStore.getUserNameById(item.assigned_to_id, false);
      if (userName) {
        item.assigned_to = userName;
      }
    }
    if (item.due_date != null && typeof item.due_date !== "string") {
      item.due_date = (item.due_date as { toISOString?: () => string }).toISOString?.() ?? String(item.due_date);
    }
  }

  /** Removes files from topics for all models queued for deletion. */
  private processModelsToDelete() {
    for (const model of this.BcfImportModelsToDelete) {
      this.removeFileFromTopic(model.guid, model.modelListId);
    }
  }

  /**
   * Sets replacement values on conflicts based on user-submitted form values.
   * Author fields keep their original value.
   */
  private resolveConflictValues(item: TopicsWithConflicts, values: API_BcfServerTopic) {
    const authorFields = new Set(["modified_author", "creation_author", "author"]);
    const conflictToFormKey: Record<string, string> = { assigned_to: "assigned_to_id" };

    for (const conflict of item.viewData.conflicts) {
      const formKey = conflictToFormKey[conflict.fieldName] ?? conflict.fieldName;

      if (!(conflict.fieldName in item) || !(formKey in values)) continue;
      conflict.replacementValue = authorFields.has(conflict.fieldName) ? conflict.originalValue : values[formKey];
    }
  }

  /** Recounts mandatory and optional warning amounts based on current unresolved conflicts. */
  private recalculateWarnings(item: TopicsWithConflicts) {
    item.viewData.mandatoryWarningsAmount = item.viewData.conflicts.filter((e) =>
      e.extensionType !== "FILE" ? !e.optional && isNil(e.replacementValue) : !e.optional
    ).length;
    item.viewData.optionalWarningsAmount = item.viewData.conflicts.filter((e) =>
      e.extensionType !== "FILE" ? !e.optional && isNil(e.replacementValue) : e.optional
    ).length;
  }

  @handleStoreState()
  async importIssue(guidsForDelete: string[]) {
    try {
      await Promise.all(
        this.bcfTempFiles.map((tempFile) => {
          const storeTopics = this.topicsWithConflicts.filter((item) => tempFile.id === item.viewData?.fileId);
          const overrides = storeTopics.map((topic) => {
            const { viewData, files, ...topicData } = toJS(topic);
            const formattedFiles = files?.map((f) => {
              const { fileStatus, ...fileData } = f;
              return { file: fileData, fileStatus };
            });
            return { ...topicData, files: formattedFiles };
          });
          const bcfBody: API_BcfImportRequest = {
            tempFile,
            overrideRequest: {
              overrides,
              removedTopicGuids: guidsForDelete,
            },
          };
          return API.newData(API_URL.BCF_SERVER_EXTENSIONS.importBcfData(projectsDataStore.projectId), bcfBody);
        })
      );
    } catch (error) {
      throw error;
    }
  }

  processTopicsAndConflictsData(file: API_UploadFile, topicsAndConflicts: API_BcfImportTopicAndConflicts) {
    const existingFileIdx = this.bcfTempFiles.findIndex((item) => item?.name === file.name);
    if (existingFileIdx !== -1) {
      const existingFile = this.bcfTempFiles[existingFileIdx];
      this.topicsWithConflicts = this.topicsWithConflicts.filter((topic) => topic.viewData?.fileId !== existingFile.id);
      this.bcfTempFiles.splice(existingFileIdx, 1);
    }

    const conflictsMap = groupBy(topicsAndConflicts.conflicts, "topicGuid");

    const topicsForTable = topicsAndConflicts.topics.map((topic) => ({
      ...topic,
      files: topic.files?.map((fileItem) => ({
        ...fileItem.file,
        fileStatus: fileItem.fileStatus,
        modelListId: fileItem.modelListId,
      })),
      viewData: {
        conflicts: conflictsMap[topic.guid] || [],
        fileId: file.id,
        mandatoryWarningsAmount: conflictsMap[topic.guid]
          ? conflictsMap[topic.guid].filter((i) => !i.optional).length
          : 0,
        optionalWarningsAmount: conflictsMap[topic.guid]
          ? conflictsMap[topic.guid].filter((i) => i.optional).length
          : 0,
      },
    }));
    runInAction(async () => {
      this.topicsWithConflicts = [...this.topicsWithConflicts, ...topicsForTable];
      this.bcfTempFiles.push(file);
    });
  }

  removeFileFromTopic(topicGuid: string, modelListId: number) {
    runInAction(async () => {
      const selectedTopic = this.topicsWithConflicts.find((item) => item.guid === topicGuid);
      const selectedFile = selectedTopic.files.find((item) => item.modelListId === modelListId);
      // removing conflict that are related to file
      selectedTopic.viewData.conflicts = selectedTopic.viewData.conflicts.filter(
        (item) => item.modelListId !== selectedFile.modelListId
      );
      // removing file that are related to topic
      selectedTopic.files = selectedTopic?.files.filter((item) => item.modelListId !== modelListId);
      selectedTopic.viewData.conflicts = this.findDuplicatesAndUpdate(selectedTopic.files, topicGuid);

      // const conflictsMap = groupBy(selectedTopic.viewData.conflicts, "topicGuid");
      // recheck mandatoryWarningsAmount for current topic
      // selectedTopic.viewData.mandatoryWarningsAmount = conflictsMap[selectedTopic.guid]?.filter(
      //   (i) => !i.optional
      // ).length;
    });
  }

  removeViewpointFromTopic(topicGuid: string, viewpointGuid: string) {
    const selectedTopic = this.topicsWithConflicts.find((item) => item.guid === topicGuid);
    selectedTopic.viewpoints = selectedTopic.viewpoints.filter((item) => item.guid !== viewpointGuid);
    return selectedTopic.viewpoints;
  }

  setModelsForDelete(guid: string, modelListId: number) {
    this.BcfImportModelsToDelete = [...this.BcfImportModelsToDelete, { guid, modelListId }];
  }

  findDuplicatesAndUpdate(files: BcfServerFileWithFileStatus[] & API_BcfImportFile[], topicGuid?: string) {
    const guidMap = new Map();
    const filenameMap = new Map();
    const topicsWithConflicts = toJS(this.topicsWithConflicts.find((item) => item.guid === topicGuid));
    const selectedTopic = cloneDeep(topicsWithConflicts);

    files.forEach((item) => {
      if (!guidMap.has(item.ifc_project)) {
        guidMap.set(item.ifc_project, []);
      }
      if (!filenameMap.has(item.filename)) {
        filenameMap.set(item.filename, []);
      }
      guidMap.get(item.ifc_project).push(item);
      filenameMap.get(item.filename).push(item);
    });

    const isUnique = (item: BcfServerFileWithFileStatus) => {
      const guidDuplicates = guidMap.get(item.ifc_project);
      const filenameDuplicates = filenameMap.get(item.filename);

      const isGuidUnique = guidDuplicates && guidDuplicates.length === 1;
      const isFilenameUnique = filenameDuplicates && filenameDuplicates.length === 1;
      return isGuidUnique && isFilenameUnique;
    };

    // Update the files items
    files.forEach((item) => {
      if (isUnique(item)) {
        selectedTopic.viewData.conflicts = selectedTopic.viewData.conflicts.filter(
          (conflict) => conflict.modelListId !== item.modelListId
        );
      }
    });
    return selectedTopic.viewData.conflicts;
  }

  resetModelsForDelete() {
    runInAction(() => {
      this.BcfImportModelsToDelete = [];
    });
  }

  setImportSubmit(value) {
    runInAction(() => {
      this.submitImport = value;
    });
  }

  resetConflictsAndFiles() {
    this.bcfTempFiles = [];
    this.topicsWithConflicts = [];
  }
}

export const bcfIssuesImportStore = new BCFIssuesImportStore();
